import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import { getConector, EstadoDoConector } from '../services/api';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useConfirmar } from '../contexts/Confirmacao';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import './ConectorModal.css';

/**
 * "Conectar ao Claude / ChatGPT" — a porta de entrada do conector.
 *
 * ── Por que esta tela existe ─────────────────────────────────────────────
 *
 * Sem ela o conector é uma URL que ninguém conhece. Ela responde, nesta
 * ordem, as quatro perguntas de quem nunca ouviu falar disso: para que serve,
 * o que só isso faz, o que sai e para quem, e como ligar.
 *
 * ── Quem sabe quem está conectado é o Supabase ───────────────────────────
 *
 * As autorizações vivem lá (`auth.oauth.listGrants`), e é lá que se revogam.
 * Guardar uma cópia disso no nosso banco criaria uma segunda verdade sobre
 * quem tem acesso aos dados dela — o pior lugar possível para duas verdades.
 * Do nosso lado vem só a data do último uso.
 *
 * ── A palavra "MCP" não aparece ──────────────────────────────────────────
 *
 * É o nome do protocolo, não do que ela ganha. Na tela isso é "conversar
 * sobre seu dinheiro no Claude ou no ChatGPT".
 */
interface Props { onClose: () => void; userId?: string }

interface Autorizacao {
  clientId: string;
  nome: string;
  desde: string;
}

const dataCurta = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

// Três, não cinco (22/10: "muito texto"): uma conta, um "e se", um lançamento.
const EXEMPLOS = [
  'Como eu quito essa dívida mais fácil?',
  'E se eu trocar de carro em março, como fica meu saldo?',
  '(foto da fatura) Lança tudo isso para mim.',
];

/*
 * O que veio, guardado por conta (22/10), como WhatsApp e Newsletter: o modal
 * abre com o que já veio e confere em silêncio; o menu do avatar já busca antes.
 */
type Lido = { estado: EstadoDoConector | null; autorizacoes: Autorizacao[] | null };
const guardados = new Map<string, Lido>();
const buscando = new Map<string, Promise<Lido>>();

const lerConector = async (): Promise<Lido> => {
  const [meu, grants] = await Promise.all([
    getConector().catch(() => null),
    supabase.auth.oauth.listGrants().catch(() => ({ data: null, error: true as const })),
  ]);
  /*
   * Lista vazia e lista indisponível são coisas diferentes: a primeira diz
   * "ninguém conectado", a segunda não diz nada. Sem separar, um erro de
   * rede viraria "você não tem nenhum aplicativo conectado" — uma frase
   * falsa dita com confiança sobre quem enxerga o dinheiro dela.
   */
  const autorizacoes = grants && 'data' in grants && Array.isArray(grants.data)
    ? grants.data.map(g => ({
        clientId: (g.client as { client_id?: string; id?: string }).client_id
          ?? (g.client as { id?: string }).id ?? '',
        nome: (g.client as { client_name?: string; name?: string }).client_name
          ?? (g.client as { name?: string }).name ?? 'Aplicativo de IA',
        desde: g.granted_at,
      }))
    : null;
  return { estado: meu, autorizacoes };
};

export const buscarConector = (userId = ''): Promise<Lido> => {
  const emCurso = buscando.get(userId);
  if (emCurso) return emCurso;
  const pedido = lerConector()
    .then(r => { guardados.set(userId, r); return r; })
    .finally(() => buscando.delete(userId));
  buscando.set(userId, pedido);
  return pedido;
};

export const ConectorModal: React.FC<Props> = ({ onClose, userId = '' }) => {
  const jaVeio = guardados.get(userId);
  const [estado, setEstado] = useState<EstadoDoConector | null>(jaVeio?.estado ?? null);
  const [autorizacoes, setAutorizacoes] = useState<Autorizacao[] | null>(jaVeio?.autorizacoes ?? null);
  const [carregando, setCarregando] = useState(!jaVeio);
  const [copiado, setCopiado] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const confirmar = useConfirmar();

  useTravarScroll();
  useFocoNoModal(caixa);
  useFechavelComVoltar(true, onClose);

  const carregar = useCallback(async () => {
    const r = await buscarConector(userId);
    setEstado(r.estado);
    setAutorizacoes(r.autorizacoes);
    setCarregando(false);
  }, [userId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const copiar = async () => {
    if (!estado?.url) return;
    try {
      await navigator.clipboard.writeText(estado.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { /* sem área de transferência: o endereço está à vista para copiar na mão. */ }
  };

  const desconectar = async (a: Autorizacao) => {
    if (!await confirmar({
      titulo: 'Desconectar este aplicativo?',
      alvo: a.nome,
      texto: 'Ele deixa de enxergar seus dados na hora. Nada é apagado, e dá para conectar de novo quando quiser.',
      acao: 'Desconectar', perigo: true,
    })) return;
    await supabase.auth.oauth.revokeGrant({ clientId: a.clientId });
    await carregar();
  };

  const conectados = autorizacoes ?? [];

  return createPortal(
    <div className="con-overlay" onClick={onClose} role="presentation" ref={caixa}>
      <div
        className="con-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="con-titulo"
      >
        <div className="con-banda">
          <span className="con-glifo" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3.2" fill="currentColor"/>
              <circle cx="4.5" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.6"/>
              <circle cx="4.5" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.6"/>
              <circle cx="20" cy="12" r="2.2" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M6.6 7.1l3 2.6M6.6 16.9l3-2.6M15.2 12h2.6"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
          <h2 id="con-titulo">Claude e ChatGPT</h2>
          <button className="con-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="con-corpo">
          {carregando ? (
            <p className="con-texto">Carregando…</p>
          ) : (
            <>
              <p className="con-texto">
                Converse sobre o seu dinheiro no Claude ou no ChatGPT, com os seus números.
              </p>

              <p className="con-subtitulo">Por exemplo</p>
              <ul className="con-exemplos">
                {EXEMPLOS.map(e => <li key={e}>{e}</li>)}
              </ul>

              {conectados.length > 0 && (
                <div className="con-conectados">
                  <p className="con-subtitulo">Conectados</p>
                  {conectados.map(a => (
                    <div className="con-linha" key={a.clientId || a.nome}>
                      <div>
                        <strong>{a.nome}</strong>
                        <span className="con-desde">desde {dataCurta(a.desde)}</span>
                      </div>
                      <button className="con-desconectar" onClick={() => void desconectar(a)}>
                        Desconectar
                      </button>
                    </div>
                  ))}
                  <p className="con-nota">
                    {estado?.ultimoUsoEm
                      ? <>Última consulta pelo conector: {dataCurta(estado.ultimoUsoEm)}.</>
                      : <>Ainda não houve nenhuma consulta pelo conector.</>}
                  </p>
                </div>
              )}

              <p className="con-subtitulo">Como ligar</p>
              {estado?.servidorPronto && estado.url ? (
                <>
                  <div className="con-url">
                    <code>{estado.url}</code>
                    <button className="con-copiar" onClick={() => void copiar()}>
                      {copiado ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                  <ol className="con-passos">
                    <li><strong>Claude:</strong> Configurações → Conectores → Adicionar personalizado.</li>
                    <li><strong>ChatGPT:</strong> Configurações → Conectores → Criar (nem todo plano tem).</li>
                    <li>Cole o endereço e autorize na tela <strong>do FinTracking</strong> que abrir.</li>
                  </ol>
                </>
              ) : (
                <p className="con-alerta">
                  O servidor ainda não sabe o próprio endereço público: falta a
                  variável <code>MCP_RESOURCE_URL</code> no Render. Sem ela o
                  conector não tem como ser adicionado.
                </p>
              )}

              {/*
                O que sai e para quem, antes de conectar e não depois.

                A política de privacidade descreve o que o app faz com os dados;
                isto aqui é o que MUDA ao conectar — eles passam a sair para uma
                empresa de IA a cada pergunta feita lá.
              */}
              <p className="con-subtitulo">Antes de ligar</p>
              <ul className="con-lista">
                <li><strong>Lê</strong> seus lançamentos, saldos, dívidas e metas, e os envia à Anthropic ou à OpenAI a cada pergunta.</li>
                <li><strong>Lança</strong> só quando você pede, mostrando antes o que vai gravar. <strong>Nunca apaga.</strong></li>
                <li>Desconecte aqui quando quiser.</li>
              </ul>

              {autorizacoes === null && (
                <p className="con-nota">
                  Não consegui verificar agora quais aplicativos estão conectados.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConectorModal;
